import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  Globe,
  Plus,
  X,
  Save,
  AlertCircle,
  ExternalLink,
  Trash2,
  Radio,
  Shield,
  CheckCircle,
  Loader2,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface DomainPattern {
  pattern: string;
  label?: string;
  isPrimary?: boolean;
  addedAt?: string;
}

interface ProjectSettings {
  allowedDomains?: DomainPattern[];
  enableNetworkCapture?: boolean;
  networkCaptureSessionLimitMinutes?: number;
}

interface Project {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  key: string;
  settings?: ProjectSettings;
}

// ============================================
// Domain Card Component
// ============================================

function DomainCard({
  domain,
  totalCount,
  onSetPrimary,
  onDelete,
}: {
  domain: DomainPattern;
  totalCount: number;
  onSetPrimary: () => void;
  onDelete: () => void;
}) {
  const getNavigableUrl = (pattern: string): string => {
    let d = pattern;
    if (d.startsWith('*.')) {
      d = d.slice(2);
    } else if (d.startsWith('*-')) {
      d = d.slice(2);
    } else if (d.includes('*')) {
      d = d.replace(/\*/g, '');
    }
    return `https://${d}`;
  };

  const getPatternType = (pattern: string): { label: string; color: string } => {
    if (pattern.startsWith('*.')) {
      return { label: 'Wildcard', color: 'var(--warning)' };
    }
    if (pattern.includes('*')) {
      return { label: 'Pattern', color: 'var(--info)' };
    }
    return { label: 'Exact', color: 'var(--success)' };
  };

  const patternType = getPatternType(domain.pattern);
  const isWide = totalCount === 1;

  // Wide layout for single card
  if (isWide) {
    return (
      <div className="card group hover:border-primary transition-all">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Left: Domain Info */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: domain.isPrimary ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'var(--surface-2)' }}>
              <Globe className="w-7 h-7" style={{ color: domain.isPrimary ? 'white' : 'var(--text-muted)' }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-1">
                <code className="text-lg font-mono text-default font-medium">{domain.pattern}</code>
                {domain.isPrimary && (
                  <span className="badge badge-success">Primary</span>
                )}
                <span 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: `${patternType.color}20`, color: patternType.color }}
                >
                  {patternType.label}
                </span>
              </div>
              {domain.label && (
                <p className="text-sm text-muted">{domain.label}</p>
              )}
            </div>
          </div>

          {/* Center: Info */}
          <div className="flex items-center gap-8 lg:gap-12">
            <div className="flex items-center gap-2 text-muted">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Extension will only work on this domain</span>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex flex-col lg:items-end gap-3">
            <div className="flex items-center gap-2">
              <a
                href={getNavigableUrl(domain.pattern)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <ExternalLink className="w-4 h-4" />
                Open
              </a>
              {!domain.isPrimary && (
                <button onClick={onSetPrimary} className="btn btn-ghost">
                  Set Primary
                </button>
              )}
              <button onClick={onDelete} className="btn btn-ghost p-2 text-danger hover:bg-danger/10" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact card layout
  return (
    <div className="card group hover:border-primary transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: domain.isPrimary ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'var(--surface-2)' }}>
            <Globe className="w-5 h-5" style={{ color: domain.isPrimary ? 'white' : 'var(--text-muted)' }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-default font-medium">
                {domain.pattern}
              </code>
            </div>
            {domain.label && (
              <p className="text-xs text-muted mt-0.5">{domain.label}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {domain.isPrimary && (
            <span className="badge badge-success text-xs">Primary</span>
          )}
          <span 
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: `${patternType.color}20`, color: patternType.color }}
          >
            {patternType.label}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-2 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <a
          href={getNavigableUrl(domain.pattern)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary flex-1"
        >
          <ExternalLink className="w-4 h-4" />
          Open
        </a>
        {!domain.isPrimary && (
          <button onClick={onSetPrimary} className="btn btn-ghost p-2" title="Set as Primary">
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
        <button onClick={onDelete} className="btn btn-ghost p-2 text-danger hover:bg-danger/10" title="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================
// Add Domain Modal
// ============================================

function AddDomainModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (domain: DomainPattern) => void;
}) {
  const [pattern, setPattern] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validatePattern = (p: string): { isValid: boolean; error?: string; warning?: string } => {
    if (!p || p.trim() === '') {
      return { isValid: false, error: 'Pattern cannot be empty' };
    }
    
    const cleaned = p.trim().toLowerCase();
    
    if (cleaned.includes('://')) {
      return { isValid: false, error: 'Do not include protocol (http/https)' };
    }
    
    if (cleaned.includes('/')) {
      return { isValid: false, error: 'Do not include path, only domain' };
    }
    
    // Check for common mistakes with wildcard patterns
    if (/^\*[a-z]/.test(cleaned)) {
      return { 
        isValid: false, 
        error: `Did you mean "*.${cleaned.slice(1)}"? Use *.domain.com for subdomains` 
      };
    }
    
    const domainPart = cleaned.replace(/^\*[\.-]?/, '');
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
    
    if (!domainRegex.test(domainPart)) {
      return { isValid: false, error: 'Invalid domain format' };
    }
    
    return { isValid: true };
  };

  const handleAdd = () => {
    const p = pattern.trim().toLowerCase();
    const validation = validatePattern(p);
    
    if (!validation.isValid) {
      setError(validation.error || 'Invalid pattern');
      return;
    }
    
    onAdd({
      pattern: p,
      label: label.trim() || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Globe className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            Add Domain Pattern
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Domain Pattern
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="example.com, *.example.com, *-preview.app"
              value={pattern}
              onChange={(e) => { setPattern(e.target.value); setError(null); }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Label <span className="text-muted font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., Production, Staging"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          
          {/* Pattern examples */}
          <div className="p-3 rounded-lg text-xs" style={{ background: 'var(--surface-2)' }}>
            <p className="font-medium text-default mb-2">Pattern examples:</p>
            <ul className="space-y-1 text-muted">
              <li><code className="text-primary">app.example.com</code> — exact domain match</li>
              <li><code className="text-primary">*.example.com</code> — any subdomain (dev, staging, etc)</li>
              <li><code className="text-primary">*-example.pages.dev</code> — preview deploys with prefixes</li>
            </ul>
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!pattern.trim()}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Network Capture Settings Section
// ============================================

function NetworkCaptureSection({
  enabled,
  sessionLimit,
  onToggle,
  onSessionLimitChange,
}: {
  enabled: boolean;
  sessionLimit: number;
  onToggle: (enabled: boolean) => void;
  onSessionLimitChange: (minutes: number) => void;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: enabled ? 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' : 'var(--surface-2)' }}
        >
          <Radio className="w-5 h-5" style={{ color: enabled ? 'white' : 'var(--text-muted)' }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="card-title">Network & Console Capture</h3>
            <span className={`badge ${enabled ? 'badge-success' : 'badge-neutral'}`}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <p className="text-xs text-muted mt-0.5">
            Capture API calls and console logs for better requirement generation
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--surface-2)' }}>
          <div className="flex-1">
            <p className="font-medium text-sm text-default">Enable Network Capture</p>
            <p className="text-xs text-muted mt-1">
              Allow the extension to capture API endpoints and console logs.
              This improves acceptance criteria quality by ~35%.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => onToggle(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 rounded-full peer bg-gray-600 peer-checked:bg-primary-500 
              peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
              peer-checked:after:translate-x-full"></div>
          </label>
        </div>

        {/* Session limit (only show when capture enabled) */}
        {enabled && (
          <div className="p-4 rounded-lg" style={{ background: 'var(--surface-2)' }}>
            <label className="block text-sm font-medium text-default mb-2">
              Session Timeout (minutes)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={5}
                max={120}
                value={sessionLimit}
                onChange={(e) => onSessionLimitChange(parseInt(e.target.value) || 30)}
                className="input w-24"
              />
              <p className="text-xs text-muted">
                Capture will automatically stop after this duration (5-120 min)
              </p>
            </div>
          </div>
        )}

        {/* Security notice */}
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--success-dim)' }}>
          <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--success)' }} />
          <div className="text-xs" style={{ color: 'var(--success)' }}>
            <p className="font-medium mb-1">Privacy Safeguards</p>
            <ul className="space-y-1 list-disc ml-4">
              <li>Only API endpoint URLs captured (no request/response bodies)</li>
              <li>All passwords, tokens, and cookies automatically redacted</li>
              <li>Capture only works on whitelisted domains</li>
              <li>Chrome shows visible "Debugging" indicator</li>
              <li>All capture sessions are logged for audit</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Page
// ============================================

export function DomainRestrictionsPage() {
  const { projectId } = useParams<{ orgId: string; projectId: string }>();
  const [showAddModal, setShowAddModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [domains, setDomains] = useState<DomainPattern[]>([]);
  const [enableNetworkCapture, setEnableNetworkCapture] = useState(false);
  const [sessionLimitMinutes, setSessionLimitMinutes] = useState(30);
  const queryClient = useQueryClient();
  const { setHeader, clearHeader } = usePageHeaderStore();
  
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  // Sync local state with server data
  useEffect(() => {
    if (project?.settings) {
      setDomains(project.settings.allowedDomains || []);
      setEnableNetworkCapture(project.settings.enableNetworkCapture || false);
      setSessionLimitMinutes(project.settings.networkCaptureSessionLimitMinutes || 30);
    }
  }, [project]);
  
  useEffect(() => {
    setHeader(
      'Domain Restrictions',
      'Restrict the browser extension to work only on specific domains',
      <Globe className="w-5 h-5" style={{ color: 'var(--warning)' }} />,
      undefined,
      {
        actions: [
          {
            id: 'add-domain',
            label: 'Add Domain',
            icon: <Plus className="w-4 h-4" />,
            variant: 'primary' as const,
            onClick: () => setShowAddModal(true),
          },
        ],
      }
    );
    return () => clearHeader();
  }, [setHeader, clearHeader]);
  
  const updateMutation = useMutation({
    mutationFn: async (settings: ProjectSettings) => {
      return api.patch(`/projects/${projectId}`, { settings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setHasChanges(false);
      
      // Notify extension to refresh context via postMessage (content script will relay)
      window.postMessage({ 
        type: 'SPECUNO_SETTINGS_UPDATED',
        projectId,
        settings: {
          allowedDomains: domains,
          enableNetworkCapture,
          networkCaptureSessionLimitMinutes: sessionLimitMinutes,
        }
      }, '*');
    },
  });

  const handleAddDomain = (domain: DomainPattern) => {
    const newDomain: DomainPattern = {
      ...domain,
      isPrimary: domains.length === 0,
      addedAt: new Date().toISOString(),
    };
    
    if (domains.some(d => d.pattern === domain.pattern)) {
      return; // Already exists
    }
    
    setDomains([...domains, newDomain]);
    setHasChanges(true);
  };

  const handleSetPrimary = (index: number) => {
    const updated = domains.map((d, i) => ({
      ...d,
      isPrimary: i === index
    }));
    setDomains(updated);
    setHasChanges(true);
  };

  const handleDelete = (index: number) => {
    const updated = domains.filter((_, i) => i !== index);
    // If we removed the primary, make first one primary
    if (domains[index].isPrimary && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    setDomains(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      allowedDomains: domains,
      enableNetworkCapture,
      networkCaptureSessionLimitMinutes: sessionLimitMinutes,
    });
  };

  const handleEnableNetworkCaptureChange = (enabled: boolean) => {
    setEnableNetworkCapture(enabled);
    setHasChanges(true);
  };

  const handleSessionLimitChange = (minutes: number) => {
    setSessionLimitMinutes(minutes);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-1/4" />
        <div className="skeleton h-48 rounded-card" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Save bar */}
      {hasChanges && (
        <div className="p-4 rounded-lg flex items-center justify-between" style={{ background: 'var(--warning-dim)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--warning)' }}>
            You have unsaved changes
          </p>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Domains Grid */}
      {domains.length > 0 ? (
        <div className={
          domains.length === 1 
            ? "grid grid-cols-1 gap-6" 
            : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        }>
          {domains.map((domain, idx) => (
            <DomainCard
              key={domain.pattern}
              domain={domain}
              totalCount={domains.length}
              onSetPrimary={() => handleSetPrimary(idx)}
              onDelete={() => handleDelete(idx)}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-16">
          <Globe className="w-16 h-16 mx-auto mb-4 text-subtle" />
          <h3 className="text-lg font-medium text-default mb-2">No domain restrictions</h3>
          <p className="text-muted mb-6 max-w-md mx-auto">
            The browser extension currently works on all pages. Add domain restrictions 
            to limit the extension to specific websites for security and focus.
          </p>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Add First Domain
          </button>
        </div>
      )}
      
      {/* Network Capture Settings (only show when domains exist) */}
      {domains.length > 0 && (
        <NetworkCaptureSection
          enabled={enableNetworkCapture}
          sessionLimit={sessionLimitMinutes}
          onToggle={handleEnableNetworkCaptureChange}
          onSessionLimitChange={handleSessionLimitChange}
        />
      )}
      
      {/* Add Modal */}
      {showAddModal && (
        <AddDomainModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddDomain}
        />
      )}
    </div>
  );
}
